import { useCallback, useState } from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

let scriptPromise = null;

// Load api.js?render=SITE_KEY exactly once.
export function loadRecaptchaScript() {
  if (!SITE_KEY) {
    return Promise.reject(new Error("reCAPTCHA site key is not configured"));
  }
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) return resolve(window.grecaptcha);

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () =>
      window.grecaptcha
        ? resolve(window.grecaptcha)
        : reject(new Error("reCAPTCHA failed to initialise"));
    script.onerror = () => {
      scriptPromise = null; // allow retry after a transient network failure
      reject(new Error("Failed to load reCAPTCHA script"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

// Fetch a FRESH token for one submission. Tokens are single-use and expire
// in ~2 minutes — never cache or reuse; always call at submit time.
export async function executeRecaptcha(action) {
  const grecaptcha = await loadRecaptchaScript();
  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(SITE_KEY, { action }).then(resolve).catch(reject);
    });
  });
}

// React hook: returns getToken(action) plus executing/error state for UX.
export function useRecaptcha() {
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);

  const getToken = useCallback(async (action) => {
    setExecuting(true);
    setError(null);
    try {
      return await executeRecaptcha(action);
    } catch (e) {
      setError(
        "Verification failed to load. Please check your connection and try again.",
      );
      return null;
    } finally {
      setExecuting(false);
    }
  }, []);

  return { getToken, executing, error };
}
